// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://jfnkjpjryejjcrihwvlt.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmbmtqcGpyeWVqamNyaWh3dmx0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTEzMjc2OTksImV4cCI6MjA2NjkwMzY5OX0.d50KRkQI7NWZIoncczRap9412X7a2oyFYmydrnGBuYA";

// Default path: talk to the external Supabase host directly — fastest, no
// extra hop. If a request fails with a network-level error (typical signature
// of an ad blocker, DNS block, or corporate proxy filter), transparently fall
// back to the Lovable Cloud proxy and pin to it for the rest of the session.
const CLOUD_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
const PROXY_BASE = CLOUD_URL ? `${CLOUD_URL}/functions/v1/external-proxy` : null;
const PROXY_FLAG_KEY = 'ledlogic-external-proxy-needed';

let useProxy = false;
try {
  useProxy = !!PROXY_BASE && sessionStorage.getItem(PROXY_FLAG_KEY) === '1';
} catch { /* ignore */ }

const toProxyUrl = (urlStr: string) =>
  PROXY_BASE ? PROXY_BASE + urlStr.slice(SUPABASE_URL.length) : urlStr;

const rebuildInput = (input: RequestInfo | URL, newUrl: string): RequestInfo | URL => {
  if (typeof input === 'string' || input instanceof URL) return newUrl;
  return new Request(newUrl, input as Request);
};

const proxiedFetch: typeof fetch = async (input, init) => {
  const urlStr = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : (input as Request).url;

  if (!urlStr.startsWith(SUPABASE_URL) || !PROXY_BASE) {
    return fetch(input as any, init);
  }

  if (useProxy) {
    return fetch(rebuildInput(input, toProxyUrl(urlStr)), init);
  }

  try {
    return await fetch(input as any, init);
  } catch {
    useProxy = true;
    try { sessionStorage.setItem(PROXY_FLAG_KEY, '1'); } catch { /* ignore */ }
    return fetch(rebuildInput(input, toProxyUrl(urlStr)), init);
  }
};

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// This client is data-only: the Lovable Cloud client (cloudClient.ts) owns
// authentication. Creating it with `accessToken` means supabase-js never spins
// up a second GoTrueClient here, which removes the "Multiple GoTrueClient
// instances detected in the same browser context" warning. Requests keep using
// the publishable key as the bearer token, exactly as before.
const dataClient = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  accessToken: async () => SUPABASE_PUBLISHABLE_KEY,
  global: {
    fetch: proxiedFetch,
  },
});

// With `accessToken` set, supabase-js replaces `.auth` with a Proxy that throws
// on any access. The auto-generated Lovable OAuth bridge calls
// `supabase.auth.setSession(...)`, so forward the few auth calls it makes to the
// client that actually holds the session.
Object.defineProperty(dataClient, 'auth', {
  configurable: true,
  value: {
    setSession: async (tokens: unknown) => {
      const { cloudClient } = await import('./cloudClient');
      return (cloudClient.auth as any).setSession(tokens);
    },
    getSession: async () => {
      const { cloudClient } = await import('./cloudClient');
      return cloudClient.auth.getSession();
    },
    getUser: async () => {
      const { cloudClient } = await import('./cloudClient');
      return cloudClient.auth.getUser();
    },
  },
});

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = dataClient;
